import React from 'react';
import { TrendingUp, Users, FileText, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { usePageHeader } from '../contexts/PageHeaderContext';
import PageHeader from '../components/PageHeader';
import { showToast } from '../utils/toast';

// Page Header configuration
const PAGE_HEADER_CONFIG = {
  breadcrumbs: [{ label: 'Dashboard' }],
  title: 'Dashboard',
  icon: '🏠',
  description: 'Welcome to your dashboard. View your stats and quick actions.',
  actionButton: {
    icon: '✨',
    label: 'New Project',
    onClick: () => showToast('New project created!', 'success')
  }
};

// Stats configuration
const stats = [
  { 
    label: 'Total Projects', 
    value: '24', 
    change: '+12%',
    changeType: 'increase',
    icon: FileText, 
    color: 'var(--color-primary)' 
  },
  { 
    label: 'Active Users', 
    value: '1,234', 
    change: '+5%',
    changeType: 'increase',
    icon: Users, 
    color: 'var(--color-success)' 
  },
  { 
    label: 'Documents', 
    value: '89', 
    change: '-2%',
    changeType: 'decrease',
    icon: FileText, 
    color: '#8b5cf6' 
  },
  { 
    label: 'Growth Rate', 
    value: '+12%', 
    change: '+3%',
    changeType: 'increase',
    icon: TrendingUp, 
    color: 'var(--color-warning)' 
  },
];

const Dashboard = () => {
  // Set page header configuration
  usePageHeader(PAGE_HEADER_CONFIG);

  return (
    <div className="space-y-6">
      <PageHeader {...PAGE_HEADER_CONFIG} />
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const ChangeIcon = stat.changeType === 'increase' ? ArrowUpRight : ArrowDownRight;
          
          return (
            <div
              key={index}
              className="rounded-xl p-6 shadow-sm transition-all duration-300 hover:shadow-md"
              style={{ 
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)'
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: stat.color + '20' }}
                >
                  <Icon className="w-6 h-6" style={{ color: stat.color }} />
                </div>
                <div className={`flex items-center text-sm font-medium ${
                  stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <ChangeIcon className="w-4 h-4 mr-1" />
                  {stat.change}
                </div>
              </div>
              <div>
                <p 
                  className="text-sm mb-1"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {stat.label}
                </p>
                <p 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--color-text)' }}
                >
                  {stat.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div 
          className="rounded-xl p-6 shadow-sm transition-colors duration-300"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--color-text)' }}
          >
            Recent Activity
          </h3>
          <div className="space-y-4">
            {[
              { user: 'John Doe', action: 'completed a task', time: '2 hours ago' },
              { user: 'Jane Smith', action: 'uploaded a document', time: '4 hours ago' },
              { user: 'Mike Johnson', action: 'created a project', time: '6 hours ago' },
            ].map((item, index) => (
              <div 
                key={index} 
                className="flex items-center space-x-4 p-3 rounded-lg transition-colors hover:bg-opacity-50"
                style={{ 
                  ':hover': { backgroundColor: 'var(--color-surface-hover)' },
                  backgroundColor: index === 0 ? 'var(--color-surface-hover)' : 'transparent'
                }}
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-primary-light)' }}
                >
                  <Users className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                </div>
                <div className="flex-1">
                  <p 
                    className="text-sm font-medium"
                    style={{ color: 'var(--color-text)' }}
                  >
                    {item.user} {item.action}
                  </p>
                  <p 
                    className="text-xs"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {item.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div 
          className="rounded-xl p-6 shadow-sm transition-colors duration-300"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--color-text)' }}
          >
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'New Document', icon: '📄' },
              { label: 'Add User', icon: '👤' },
              { label: 'Analytics', icon: '📊' },
              { label: 'Settings', icon: '⚙️' },
            ].map((action, index) => (
              <button
                key={index}
                onClick={() => showToast(`${action.label} clicked`, 'info')}
                className="p-4 rounded-lg transition-all text-center hover:shadow-md"
                style={{ 
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              >
                <span className="text-2xl mb-2 block">{action.icon}</span>
                <span className="text-sm font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
